import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Users, Plus, Search, Eye, Pencil, Trash2, Upload, KeyRound,
  BookOpen, RefreshCw, BookText, Printer, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrgId } from '../../hooks/useOrgId';
import { useT } from '../../i18n/LanguageContext';
import { useFiscalYear } from '../../context/FiscalYearContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, LoadingState, Card } from '../../components/school/ui';
import { Pagination, SortableTH } from '../../components/school/Pagination';
import { formatXOF } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import LedgerDialog from '../../components/LedgerDialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getName = (s, lang) => {
  if (!s) return '';
  if (lang === 'fr') return s.name_fr || s.name_en || s.name || s.name_pt || '';
  if (lang === 'pt') return s.name_pt || s.name_en || s.name || s.name_fr || '';
  return s.name_en || s.name || s.name_fr || s.name_pt || '';
};

const emptyForm = {
  name: '', name_fr: '', name_pt: '', email: '', mobile: '', qualification: '',
  gender: 'Female', joining_date: '', address: '', notes: '', password: '',
};

// ==================== Teacher ID Card (credential card) helpers ====================
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const resolveLogo = (org) => {
  const u = org?.logo_url;
  if (!u) return '/eli-crest.png';
  return u.startsWith('http') ? u : `${BACKEND}${u}`;
};

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

const USER_SVG = '<svg viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';
const LOCK_SVG = '<svg viewBox="0 0 24 24"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>';
const GLOBE_SVG = '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>';

const CARD_CSS = `
.tcard { width: 430px; box-sizing: border-box; border-radius: 16px; overflow: hidden; background: #fff; border: 3px solid #16316b; position: relative; color: #16316b; }
.tcard * { box-sizing: border-box; }
.tcard-top { position: relative; display: flex; align-items: center; gap: 12px; padding: 18px 20px 14px; background: #fff; overflow: hidden; }
.tcard-top::after { content: ''; position: absolute; top: -40px; right: -60px; width: 170px; height: 150px; background: #16316b; transform: rotate(32deg); opacity: 0.08; }
.tcard-logo { width: 46px; height: 46px; object-fit: contain; flex: none; z-index: 1; }
.tcard-school-name { font-size: 20px; font-weight: 800; letter-spacing: 1px; line-height: 1.1; z-index: 1; }
.tcard-school-sub { font-size: 10px; font-weight: 600; letter-spacing: 3px; color: #5b6b8c; margin-top: 3px; }
.tcard-divider { height: 3px; background: linear-gradient(90deg, #16316b 62%, #c8a24a 62%); }
.tcard-body { padding: 14px 20px 16px; }
.tcard-type { font-size: 11px; font-weight: 800; letter-spacing: 2px; color: #c8a24a; text-transform: uppercase; }
.tcard-name { font-size: 24px; font-weight: 800; color: #16316b; margin: 2px 0 12px; line-height: 1.1; }
.tcard-creds { background: #f4f5f7; border-radius: 12px; border-left: 5px solid #c8a24a; padding: 12px 14px; display: flex; flex-direction: column; gap: 10px; }
.cred-row { display: flex; align-items: center; gap: 12px; }
.cred-tile { width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex: none; }
.cred-tile.user { background: #16316b; }
.cred-tile.lock { background: #c0392b; }
.cred-tile svg { width: 18px; height: 18px; stroke: #fff; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.cred-label { font-size: 9px; font-weight: 700; letter-spacing: 1.5px; color: #8a93a6; text-transform: uppercase; }
.cred-val { font-size: 15px; font-weight: 700; color: #16316b; font-family: 'Source Code Pro', monospace; word-break: break-all; }
.tcard-footer { background: #16316b; color: #fff; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 10px; }
.tcard-footer svg { width: 15px; height: 15px; stroke: #fff; fill: none; stroke-width: 2; }
.tcard-footer .portal { color: #c8a24a; font-weight: 700; letter-spacing: 2px; font-size: 11px; }
.tcard-footer .domain { font-weight: 800; font-family: 'Source Code Pro', monospace; font-size: 14px; }
`;

const cardMarkup = (org, cred) => {
  const logo = resolveLogo(org);
  const name = escapeHtml(cred?.name_en || cred?.name || '');
  const email = escapeHtml(cred?.email || '');
  const pass = escapeHtml(cred?.password || '');
  const schoolName = escapeHtml(org?.name || 'E.L.I');
  const domain = escapeHtml((cred?.email || '').split('@')[1] || 'eli-school.com');
  return `
  <div class="tcard">
    <div class="tcard-top">
      <img class="tcard-logo" src="${logo}" alt="logo" onerror="this.style.display='none'"/>
      <div>
        <div class="tcard-school-name">${schoolName}</div>
        <div class="tcard-school-sub">STAFF PORTAL</div>
      </div>
    </div>
    <div class="tcard-divider"></div>
    <div class="tcard-body">
      <div class="tcard-type">TEACHER ID CARD</div>
      <div class="tcard-name">${name}</div>
      <div class="tcard-creds">
        <div class="cred-row">
          <span class="cred-tile user">${USER_SVG}</span>
          <div><div class="cred-label">USERNAME</div><div class="cred-val">${email || '—'}</div></div>
        </div>
        <div class="cred-row">
          <span class="cred-tile lock">${LOCK_SVG}</span>
          <div><div class="cred-label">PASSWORD</div><div class="cred-val">${pass || '—'}</div></div>
        </div>
      </div>
    </div>
    <div class="tcard-footer">${GLOBE_SVG}<span class="portal">LOGIN PORTAL</span><span class="domain">${domain}</span></div>
  </div>`;
};

const buildCredentialCardsHtml = (org, creds) => {
  const cards = (creds || []).map((c) => cardMarkup(org, c)).join('');
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
  <title>Teacher Credential Cards</title>
  <link href="https://fonts.googleapis.com/css2?family=Figtree:wght@400;600;700;800&family=Source+Code+Pro:wght@600;700&display=swap" rel="stylesheet"/>
  <style>
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { margin: 0; padding: 20px; background: #eef0f4; font-family: 'Figtree', system-ui, sans-serif; }
    .grid { display: flex; flex-wrap: wrap; gap: 20px; }
    .tcard { page-break-inside: avoid; }
    ${CARD_CSS}
    @media print { body { background: #fff; padding: 0; } .grid { gap: 14px; } }
  </style></head>
  <body><div class="grid">${cards}</div></body></html>`;
};

// Print sheet: 8 credential cards per A4 page (2 columns x 4 rows).
const buildCredentialSheetHtml = (org, creds) => {
  const groups = [];
  for (let i = 0; i < (creds || []).length; i += 8) groups.push(creds.slice(i, i + 8));
  const pagesHtml = groups.map((group) => (
    `<div class="page"><div class="sheet">${group.map((c) => `<div class="cell">${cardMarkup(org, c)}</div>`).join('')}</div></div>`
  )).join('');
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
  <title>Teacher Credential Cards</title>
  <link href="https://fonts.googleapis.com/css2?family=Figtree:wght@400;600;700;800&family=Source+Code+Pro:wght@600;700&display=swap" rel="stylesheet"/>
  <style>
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    @page { size: A4 portrait; margin: 8mm; }
    body { margin: 0; padding: 0; background: #fff; font-family: 'Figtree', system-ui, sans-serif; }
    ${CARD_CSS}
    .page { width: 100%; }
    .sheet { display: grid; grid-template-columns: 1fr 1fr; grid-auto-rows: 64mm; gap: 4mm; }
    .cell { width: 92mm; height: 63mm; overflow: hidden; }
    .cell .tcard { width: 430px; transform: scale(0.72); transform-origin: top left; border-width: 2px; }
    .page + .page { page-break-before: always; }
  </style></head>
  <body>${pagesHtml}</body></html>`;
};


export default function TeachersPage() {
  const { user, canAdmin, currentOrg } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const { t, lang } = useT();
  const orgId = useOrgId();
  const { selectedFY } = useFiscalYear();
  const fileRef = useRef(null);

  const [staff, setStaff] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const PAGE_SIZE = 50;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [nextCode, setNextCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [viewStaff, setViewStaff] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [ledgerAccount, setLedgerAccount] = useState(null);

  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);

  // Credential generation
  const [credOpen, setCredOpen] = useState(false);
  const [credScan, setCredScan] = useState([]);
  const [credLoading, setCredLoading] = useState(false);
  const [credSearch, setCredSearch] = useState('');
  const [credSelected, setCredSelected] = useState({});
  const [credShowAll, setCredShowAll] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [printCards, setPrintCards] = useState(null); // array of creds -> print preview
  const [revealCred, setRevealCred] = useState(null); // {id,email,password} in view dialog

  // Reprint already-generated credentials
  const [reprintOpen, setReprintOpen] = useState(false);
  const [reprintList, setReprintList] = useState([]);
  const [reprintLoading, setReprintLoading] = useState(false);
  const [reprintSearch, setReprintSearch] = useState('');
  const [reprintSelected, setReprintSelected] = useState({});

  const load = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    try {
      setLoading(true);
      const data = await schoolApi.listStaff(orgId, {
        search, sort_by: sortBy, sort_dir: sortDir,
        skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE,
      });
      setStaff(data.staff || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error('Failed to load staff'); }
    finally { setLoading(false); }
  }, [orgId, search, sortBy, sortDir, page]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);
  useEffect(() => { setPage(1); }, [search, sortBy, sortDir]);

  const onSort = (field) => {
    if (sortBy === field) { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); }
    else { setSortBy(field); setSortDir('asc'); }
  };

  const openCreate = async () => {
    setEditing(null); setForm(emptyForm); setDialogOpen(true);
    try { const r = await schoolApi.staffNextCode(orgId); setNextCode(r.next_code); } catch { setNextCode(''); }
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name_en || s.name || '', name_fr: s.name_fr || '', name_pt: s.name_pt || '',
      email: s.email || '', mobile: s.mobile || '', qualification: s.qualification || '',
      gender: s.gender || 'Female', joining_date: s.joining_date || '', address: s.address || '',
      notes: s.notes || '', password: '',
    });
    setNextCode(s.code);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error(t('common.name', 'Name') + ' ' + t('common.required', 'is required')); return; }
    setSaving(true);
    try {
      const payload = {
        organization_id: orgId,
        name: form.name.trim(),
        name_fr: form.name_fr.trim() || null,
        name_pt: form.name_pt.trim() || null,
        email: form.email.trim() || null,
        mobile: form.mobile.trim() || null,
        qualification: form.qualification.trim() || null,
        gender: form.gender || null,
        joining_date: form.joining_date || null,
        address: form.address.trim() || null,
        notes: form.notes.trim() || null,
        password: form.password ? form.password : null,
      };
      if (editing) { await schoolApi.updateStaff(editing.id, payload); toast.success(t('common.saved', 'Saved')); }
      else { await schoolApi.createStaff(payload); toast.success(t('staff.created', 'Teacher/Staff created')); }
      setDialogOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try {
      await schoolApi.deleteStaff(deleteTarget.id);
      toast.success(t('common.deleted', 'Deleted'));
      setDeleteTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete'); }
  };

  const runMigration = async () => {
    try { const r = await schoolApi.migrateStaff(orgId); toast.success(r.message); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Migration failed'); }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file); setImportPreview(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/school/staff/import-excel/preview?organization_id=${orgId}`, fd);
      setImportPreview(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to preview file'); setImportFile(null); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const runImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportProgress({ status: 'processing', processed: 0, total: importPreview?.total_rows || 0 });
    try {
      const fd = new FormData(); fd.append('file', importFile);
      const r = await axios.post(`${API}/school/staff/import-excel?organization_id=${orgId}`, fd);
      const jobId = r.data.job_id;
      if (!jobId) throw new Error('No job started');
      let done = false; let polls = 0; const maxPolls = 400;
      while (!done && polls < maxPolls) {
        polls += 1;
        await new Promise((res) => setTimeout(res, 1500));
        let job;
        try { const jr = await axios.get(`${API}/school/staff/import-jobs/${jobId}`); job = jr.data; } catch { continue; }
        setImportProgress({ status: job.status, processed: job.processed || 0, total: job.total_ops || job.total || 0 });
        if (job.status === 'completed') {
          done = true; toast.success(job.message || 'Import complete');
          setImportOpen(false); setImportPreview(null); setImportFile(null); setImportProgress(null); load();
        } else if (job.status === 'failed') {
          done = true; toast.error(job.message || 'Import failed'); setImportProgress(null);
        }
      }
      if (!done) { toast.info(t('staff.importBackground', 'Import still running in the background. Refresh shortly.')); setImportProgress(null); }
    } catch (err) { toast.error(err?.response?.data?.detail || 'Import failed'); setImportProgress(null); }
    finally { setImporting(false); }
  };

  // ---------- Credential generation ----------
  const openCredentials = async () => {
    setCredOpen(true); setCredSelected({}); setCredSearch(''); setCredShowAll(false);
    setCredLoading(true);
    try { const r = await schoolApi.scanStaffCredentials(orgId); setCredScan(r.staff || []); }
    catch { toast.error(t('staff.credScanFail', 'Failed to load teachers')); }
    finally { setCredLoading(false); }
  };

  const credFiltered = () => {
    const q = credSearch.trim().toLowerCase();
    return credScan.filter((s) => {
      if (!credShowAll && s.has_credentials) return false;
      if (q) {
        const nm = (getName(s, lang) || '').toLowerCase();
        const em = (s.email || '').toLowerCase();
        if (!nm.includes(q) && !em.includes(q)) return false;
      }
      return true;
    });
  };

  const selectedIds = Object.keys(credSelected).filter((id) => credSelected[id]);

  const toggleSelectAll = (checked) => {
    const next = { ...credSelected };
    credFiltered().forEach((s) => { next[s.id] = checked; });
    setCredSelected(next);
  };

  const runGenerate = async () => {
    if (selectedIds.length === 0) { toast.error(t('staff.credSelectOne', 'Select at least one teacher')); return; }
    setGenerating(true);
    try {
      const r = await schoolApi.generateStaffCredentials(orgId, selectedIds);
      toast.success(t('staff.credDone', `${r.count} credential(s) generated`));
      setCredOpen(false);
      setPrintCards(r.generated || []);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('common.error', 'Failed')); }
    finally { setGenerating(false); }
  };

  const revealCredentials = async (s) => {
    try {
      const r = await schoolApi.getStaffCredentials(s.id);
      if (!r.password) { toast.info(t('staff.credNone', 'No credentials generated yet for this teacher.')); return; }
      setRevealCred({ ...r, id: s.id });
    } catch (e) { toast.error(e?.response?.data?.detail || t('common.error', 'Failed')); }
  };

  const printCardsNow = () => {
    if (!printCards || printCards.length === 0) return;
    const html = buildCredentialCardsHtml(currentOrg, printCards);
    const w = window.open('', '_blank');
    if (!w) { toast.error(t('staff.popupBlocked', 'Popup blocked. Allow popups to print.')); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* noop */ } }, 500);
  };

  // ---------- Reprint already-generated credentials ----------
  const openReprint = async () => {
    setReprintOpen(true); setReprintSearch('');
    setReprintLoading(true);
    try {
      const r = await schoolApi.listStaffWithCredentials(orgId);
      const list = r.staff || [];
      setReprintList(list);
      const sel = {};
      list.forEach((s) => { sel[s.id] = true; }); // default: all selected
      setReprintSelected(sel);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('staff.reprintFail', 'Failed to load credentials'));
    } finally { setReprintLoading(false); }
  };

  const reprintFiltered = () => {
    const q = reprintSearch.trim().toLowerCase();
    if (!q) return reprintList;
    return reprintList.filter((s) => {
      const nm = (getName(s, lang) || '').toLowerCase();
      const em = (s.email || '').toLowerCase();
      return nm.includes(q) || em.includes(q);
    });
  };

  const reprintSelectedIds = Object.keys(reprintSelected).filter((id) => reprintSelected[id]);

  const toggleReprintSelectAll = (checked) => {
    const next = { ...reprintSelected };
    reprintFiltered().forEach((s) => { next[s.id] = checked; });
    setReprintSelected(next);
  };

  const printSheetNow = () => {
    const chosen = reprintList.filter((s) => reprintSelected[s.id]);
    if (chosen.length === 0) { toast.error(t('staff.reprintSelectOne', 'Select at least one teacher')); return; }
    const html = buildCredentialSheetHtml(currentOrg, chosen);
    const w = window.open('', '_blank');
    if (!w) { toast.error(t('staff.popupBlocked', 'Popup blocked. Allow popups to print.')); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* noop */ } }, 500);
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      <PageHeader
        title={t('nav.teachers', 'Teacher/Staff')}
        subtitle={t('staff.subtitle', 'Manage teachers & staff (4211 accounts), portal logins, and view ledgers')}
        icon={Users}
        actions={canAdmin ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-2" data-testid="staff-import-button"><Upload className="w-4 h-4" /> {t('common.import', 'Import')}</Button>
            <Button variant="outline" onClick={openCredentials} className="gap-2" data-testid="staff-generate-credentials-button"><KeyRound className="w-4 h-4" /> {t('staff.generateCreds', 'Generate Credentials')}</Button>
            {isSuperAdmin && (
              <Button variant="outline" onClick={openReprint} className="gap-2" data-testid="staff-print-credentials-button"><Printer className="w-4 h-4" /> {t('staff.printCreds', 'Print Credentials')}</Button>
            )}
            <Button onClick={openCreate} className="gap-2" data-testid="staff-new-button"><Plus className="w-4 h-4" /> {t('staff.new', 'New Teacher/Staff')}</Button>
          </div>
        ) : null}
      />

      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder={t('common.search', 'Search by name, code, phone, email...')} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="staff-search-input" />
          </div>
          <div className="flex items-center text-sm text-muted-foreground px-2">{total} {t('staff.total', 'staff')}</div>
        </div>
      </Card>

      <Card>
        {loading ? <div className="p-4"><LoadingState /></div> : staff.length === 0 ? (
          <EmptyState icon={Users} title={t('common.noData', 'No staff yet')}
            description={t('staff.empty', 'Create a Teacher/Staff or migrate from existing teachers.')}
            actionLabel={canAdmin ? t('staff.new', 'New Teacher/Staff') : undefined} onAction={canAdmin ? openCreate : undefined} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <SortableTH field="code" label={t('staff.code', 'Code')} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                  <SortableTH field="name_en" label={t('common.name', 'Name')} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                  <SortableTH field="email" label={t('common.email', 'Email')} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                  <SortableTH field="mobile" label={t('common.phone', 'Phone')} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                  <th className="px-4 py-3">{t('teachers.qualification', 'Qualification')}</th>
                  <th className="px-4 py-3 text-center">{t('teachers.subjects', 'Subjects')}</th>
                  <th className="px-4 py-3 text-center">{t('staff.login', 'Login')}</th>
                  <th className="px-4 py-3 text-right">{t('staff.balance', 'Balance')}</th>
                  <th className="px-4 py-3 text-right">{t('common.actions', 'Actions')}</th>
                </tr></thead>
                <tbody>
                  {staff.map((s) => (
                    <tr key={s.id} className="border-b border-border last:border-0 hover:bg-muted/40" data-testid={`staff-row-${s.id}`}>
                      <td className="px-4 py-3 font-mono text-muted-foreground">{s.code}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-semibold text-emerald-700">{getName(s, lang)?.charAt(0)}</div>
                          <div>
                            <span className="font-medium">{getName(s, lang)}</span>
                            {lang !== 'en' && (s.name_en || s.name) && <p className="text-xs text-muted-foreground">{s.name_en || s.name}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{s.email || '-'}</td>
                      <td className="px-4 py-3">{s.mobile || '-'}</td>
                      <td className="px-4 py-3">{s.qualification || '-'}</td>
                      <td className="px-4 py-3 text-center"><Badge variant="outline" className="gap-1"><BookOpen className="w-3 h-3" /> {s.subject_count ?? 0}</Badge></td>
                      <td className="px-4 py-3 text-center">
                        {s.has_login
                          ? <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50 gap-1"><KeyRound className="w-3 h-3" /> {t('staff.yes', 'Yes')}</Badge>
                          : <Badge variant="outline" className="text-muted-foreground">{t('staff.no', 'No')}</Badge>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatXOF(s.balance_usd || 0)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost" size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted"
                            onClick={() => setViewStaff(s)}
                            title={t('common.view', 'View')} aria-label={t('common.view', 'View')}
                            data-testid={`staff-view-${s.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                            onClick={() => setLedgerAccount(s)}
                            title={t('staff.viewLedger', 'View Ledger')} aria-label={t('staff.viewLedger', 'View Ledger')}
                            data-testid={`staff-ledger-${s.id}`}
                          >
                            <BookText className="w-4 h-4" />
                          </Button>
                          {canAdmin && (
                            <Button
                              variant="ghost" size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10"
                              onClick={() => openEdit(s)}
                              title={t('common.edit', 'Edit')} aria-label={t('common.edit', 'Edit')}
                              data-testid={`staff-edit-${s.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                          {canAdmin && (
                            <Button
                              variant="ghost" size="icon"
                              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                              onClick={() => setDeleteTarget(s)}
                              title={t('common.delete', 'Delete')} aria-label={t('common.delete', 'Delete')}
                              data-testid={`staff-delete-${s.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
          </>
        )}
      </Card>

      {/* Create / Edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="staff-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? t('staff.edit', 'Edit Teacher/Staff') : t('staff.new', 'New Teacher/Staff')}</DialogTitle>
            <DialogDescription>{editing ? t('staff.code', 'Code') : t('staff.nextCode', 'New account code')}: <span className="font-mono">{nextCode || '—'}</span></DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>{t('staff.nameEn', 'Name (English)')} *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" /></div>
            <div><Label>{t('staff.nameFr', 'Name (French)')}</Label><Input value={form.name_fr} onChange={(e) => setForm({ ...form, name_fr: e.target.value })} data-testid="staff-name-fr-input" /></div>
            <div><Label>{t('staff.namePt', 'Name (Portuguese)')}</Label><Input value={form.name_pt} onChange={(e) => setForm({ ...form, name_pt: e.target.value })} data-testid="staff-name-pt-input" /></div>
            <div><Label>{t('common.phone', 'Phone')}</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="staff-mobile-input" /></div>
            <div><Label>{t('teachers.qualification', 'Qualification')}</Label><Input value={form.qualification} onChange={(e) => setForm({ ...form, qualification: e.target.value })} /></div>
            <div>
              <Label>{t('common.gender', 'Gender')}</Label>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Male">{t('common.male', 'Male')}</SelectItem><SelectItem value="Female">{t('common.female', 'Female')}</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>{t('teachers.joiningDate', 'Joining Date')}</Label><Input type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} /></div>
          </div>
          <div className="border-t border-border pt-4 mt-2">
            <p className="text-sm font-semibold mb-1 flex items-center gap-2"><KeyRound className="w-4 h-4" /> {t('staff.portalLogin', 'Portal Login (Teacher role)')}</p>
            <p className="text-[11px] text-muted-foreground mb-2">
              {editing ? t('staff.loginEditHint', 'Set a password to (re)create/reset the login. Email below is the username.')
                : t('staff.loginCreateHint', 'Enter email + password to create a teacher login.')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>{t('common.email', 'Email')}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email-input" /></div>
              <div><Label>{t('people.password', 'Password')}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing && form.email ? '••••••' : ''} data-testid="staff-password-input" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={save} disabled={saving} data-testid="staff-save-button">{saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View */}
      <Dialog open={!!viewStaff} onOpenChange={(o) => { if (!o) { setViewStaff(null); setRevealCred(null); } }}>
        <DialogContent data-testid="staff-view-dialog">
          <DialogHeader><DialogTitle>{viewStaff ? getName(viewStaff, lang) : ''}</DialogTitle></DialogHeader>
          {viewStaff && (
            <div className="space-y-2 text-sm">
              <Row label={t('staff.code', 'Code')} value={viewStaff.code} />
              <Row label={t('common.email', 'Email')} value={viewStaff.email} />
              <Row label={t('common.phone', 'Phone')} value={viewStaff.mobile} />
              <Row label={t('teachers.qualification', 'Qualification')} value={viewStaff.qualification} />
              <Row label={t('common.gender', 'Gender')} value={viewStaff.gender} />
              <Row label={t('teachers.joiningDate', 'Joining Date')} value={viewStaff.joining_date} />
              <Row label={t('teachers.subjects', 'Subjects')} value={String(viewStaff.subject_count ?? 0)} />
              <Row label={t('staff.login', 'Login')} value={viewStaff.has_login ? t('staff.yes', 'Yes') : t('staff.no', 'No')} />
              <Row label={t('staff.balance', 'Balance')} value={formatXOF(viewStaff.balance_usd || 0)} />

              {isSuperAdmin && (
                <div className="border-t border-border pt-3 mt-2">
                  <p className="text-sm font-semibold mb-2 flex items-center gap-2"><KeyRound className="w-4 h-4" /> {t('staff.loginCredentials', 'Login Credentials')}</p>
                  {revealCred && revealCred.id === viewStaff.id ? (
                    <div className="space-y-2" data-testid="staff-revealed-creds">
                      <div className="rounded-lg bg-muted/60 border border-border p-3 space-y-1.5 font-mono text-[13px]">
                        <div className="flex justify-between gap-3"><span className="text-muted-foreground">{t('common.email', 'Username')}</span><span className="font-semibold break-all">{revealCred.email || '—'}</span></div>
                        <div className="flex justify-between gap-3"><span className="text-muted-foreground">{t('people.password', 'Password')}</span><span className="font-semibold break-all">{revealCred.password || '—'}</span></div>
                      </div>
                      <Button size="sm" className="gap-2" onClick={() => setPrintCards([revealCred])} data-testid="staff-reprint-card-button"><Printer className="w-4 h-4" /> {t('staff.printCard', 'Print Card')}</Button>
                    </div>
                  ) : viewStaff.has_credentials ? (
                    <Button size="sm" variant="outline" className="gap-2" onClick={() => revealCredentials(viewStaff)} data-testid="staff-reveal-creds-button"><Eye className="w-4 h-4" /> {t('staff.revealPrint', 'Reveal & Print Card')}</Button>
                  ) : (
                    <p className="text-xs text-muted-foreground">{t('staff.noCredsYet', 'No credentials generated yet. Use the "Generate Credentials" tool in the toolbar.')}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.delete', 'Delete')}?</AlertDialogTitle>
            <AlertDialogDescription>{t('staff.deleteHint', 'This removes the Teacher/Staff account and its login, and unassigns their subjects. This cannot be undone.')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-staff">{t('common.delete', 'Delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import */}
      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportPreview(null); setImportFile(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="staff-import-dialog">
          <DialogHeader>
            <DialogTitle>{t('staff.importTitle', 'Import Teachers/Staff')}</DialogTitle>
            <DialogDescription>{t('staff.importHint', 'Upload an .xlsx with columns: Full Name, New Email, Password, gender_id, date_of_joining, mobile, qualification. Existing staff are matched by email and updated; new ones get a 4211 code and a Teacher login.')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <Button variant="outline" onClick={runMigration} className="gap-2" data-testid="staff-migrate-button"><RefreshCw className="w-4 h-4" /> {t('staff.migrate', 'Migrate from existing teachers')}</Button>
            </div>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onPickFile} className="hidden" id="staff-file-input" data-testid="staff-file-input" />
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="staff-choose-file-button">{importFile ? importFile.name : t('staff.chooseFile', 'Choose Excel file')}</Button>
            </div>
            {importPreview && (
              <div className="space-y-2">
                <div className="flex gap-4 text-sm">
                  <span><strong>{importPreview.total_rows}</strong> {t('staff.rows', 'rows')}</span>
                  <span className="text-emerald-600"><strong>{importPreview.new_staff}</strong> {t('staff.toCreate', 'to create')}</span>
                  <span className="text-amber-600"><strong>{importPreview.update_staff}</strong> {t('staff.toUpdate', 'to update')}</span>
                </div>
                <div className="max-h-60 overflow-y-auto border border-border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0"><tr>
                      <th className="px-2 py-1 text-left">{t('staff.action', 'Action')}</th>
                      <th className="px-2 py-1 text-left">{t('common.name', 'Name')}</th>
                      <th className="px-2 py-1 text-left">{t('common.email', 'Email')}</th>
                      <th className="px-2 py-1 text-left">{t('common.phone', 'Phone')}</th>
                    </tr></thead>
                    <tbody>
                      {importPreview.preview_data.map((r, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-2 py-1"><Badge variant="outline" className={r.action === 'create' ? 'text-emerald-700' : 'text-amber-700'}>{r.action}</Badge></td>
                          <td className="px-2 py-1">{r.name}</td>
                          <td className="px-2 py-1">{r.email || '-'}</td>
                          <td className="px-2 py-1">{r.mobile || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {importProgress && (
              <div className="space-y-1" data-testid="staff-import-progress">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{importProgress.status === 'processing' ? t('staff.importing', 'Importing...') : importProgress.status}</span>
                  <span>{importProgress.processed}{importProgress.total ? ` / ${importProgress.total}` : ''}</span>
                </div>
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-primary transition-all duration-300" style={{ width: importProgress.total ? `${Math.min(100, Math.round((importProgress.processed / importProgress.total) * 100))}%` : '40%' }} />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} disabled={importing}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={runImport} disabled={!importPreview || importing} data-testid="staff-run-import-button">{importing ? t('common.importing', 'Importing...') : t('staff.confirmImport', 'Confirm Import')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LedgerDialog
        account={ledgerAccount}
        organizationId={orgId}
        open={!!ledgerAccount}
        onClose={() => setLedgerAccount(null)}
        userRole={user?.role}
        fyId={selectedFY?.id}
      />

      {/* Generate Credentials */}
      <Dialog open={credOpen} onOpenChange={setCredOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="staff-credentials-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5" /> {t('staff.generateCreds', 'Generate Credentials')}</DialogTitle>
            <DialogDescription>{t('staff.credDesc', 'Select teachers to generate a username (email) and a secure password. Generated cards can be printed right after.')}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder={t('staff.credSearch', 'Filter by name or email...')} value={credSearch} onChange={(e) => setCredSearch(e.target.value)} className="pl-9" data-testid="staff-cred-search" />
            </div>
            <label className="flex items-center gap-2 text-sm whitespace-nowrap cursor-pointer select-none px-1">
              <Checkbox checked={credShowAll} onCheckedChange={(v) => setCredShowAll(!!v)} data-testid="staff-cred-showall" />
              {t('staff.credShowAll', 'Include already-generated')}
            </label>
          </div>

          {credShowAll && (
            <div className="flex items-start gap-2 text-xs rounded-md border border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:border-amber-900/50 dark:text-amber-300 p-2">
              <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
              <span>{t('staff.credRegenWarn', 'Regenerating overwrites the current password — the teacher must use the new one to sign in.')}</span>
            </div>
          )}

          <div className="flex items-center justify-between border-y border-border py-2">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none">
              <Checkbox
                checked={credFiltered().length > 0 && credFiltered().every((s) => credSelected[s.id])}
                onCheckedChange={(v) => toggleSelectAll(!!v)}
                data-testid="staff-cred-selectall"
              />
              {t('staff.credSelectAll', 'Select All')}
            </label>
            <span className="text-xs text-muted-foreground" data-testid="staff-cred-selected-count">{selectedIds.length} {t('staff.credSelected', 'selected')}</span>
          </div>

          <div className="flex-1 overflow-y-auto -mx-1 px-1">
            {credLoading ? (
              <div className="py-8"><LoadingState /></div>
            ) : credFiltered().length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {credShowAll ? t('staff.credEmptyAll', 'No teachers found.') : t('staff.credEmpty', 'All teachers already have credentials. Toggle "Include already-generated" to regenerate.')}
              </div>
            ) : (
              <div className="divide-y divide-border">
                {credFiltered().map((s) => (
                  <label key={s.id} className="flex items-center gap-3 py-2.5 cursor-pointer hover:bg-muted/40 rounded px-1" data-testid={`staff-cred-row-${s.id}`}>
                    <Checkbox checked={!!credSelected[s.id]} onCheckedChange={() => setCredSelected((p) => ({ ...p, [s.id]: !p[s.id] }))} data-testid={`staff-cred-check-${s.id}`} />
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-semibold text-emerald-700 flex-none">{(getName(s, lang) || '?').charAt(0)}</div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{getName(s, lang)}</div>
                      <div className="text-xs text-muted-foreground truncate">{s.email || t('staff.credNoEmail', 'No email — one will be generated')}</div>
                    </div>
                    {s.has_credentials && <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50 gap-1"><KeyRound className="w-3 h-3" /> {t('staff.credHas', 'Generated')}</Badge>}
                  </label>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCredOpen(false)} disabled={generating}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={runGenerate} disabled={generating || selectedIds.length === 0} className="gap-2" data-testid="staff-cred-generate-button">
              <KeyRound className="w-4 h-4" /> {generating ? t('staff.credGenerating', 'Generating...') : t('staff.credGenerateBtn', `Generate (${selectedIds.length})`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Preview */}
      <Dialog open={!!printCards} onOpenChange={(o) => { if (!o) setPrintCards(null); }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="staff-print-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Printer className="w-5 h-5" /> {t('staff.printPreview', 'Credential Cards — Print Preview')}</DialogTitle>
            <DialogDescription>{(printCards?.length || 0)} {t('staff.cardsCount', 'card(s) ready to print.')}</DialogDescription>
          </DialogHeader>
          <style dangerouslySetInnerHTML={{ __html: CARD_CSS }} />
          <div className="flex-1 overflow-y-auto bg-muted/40 rounded-lg p-4">
            <div className="flex flex-wrap gap-5 justify-center">
              {(printCards || []).map((c) => (
                <div key={c.id} dangerouslySetInnerHTML={{ __html: cardMarkup(currentOrg, c) }} />
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrintCards(null)}>{t('common.close', 'Close')}</Button>
            <Button onClick={printCardsNow} className="gap-2" data-testid="staff-print-button"><Printer className="w-4 h-4" /> {t('staff.printAll', 'Print')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reprint already-generated credentials */}
      <Dialog open={reprintOpen} onOpenChange={setReprintOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="staff-reprint-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Printer className="w-5 h-5" /> {t('staff.printCreds', 'Print Credentials')}</DialogTitle>
            <DialogDescription>{t('staff.reprintDesc', 'Reprint ID cards for teachers who already have credentials. Select the ones to print — cards are laid out 8 per A4 page.')}</DialogDescription>
          </DialogHeader>
          <style dangerouslySetInnerHTML={{ __html: CARD_CSS }} />

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder={t('staff.credSearch', 'Filter by name or email...')} value={reprintSearch} onChange={(e) => setReprintSearch(e.target.value)} className="pl-9" data-testid="staff-reprint-search" />
            </div>
            <label className="flex items-center gap-2 text-sm font-medium whitespace-nowrap cursor-pointer select-none px-1">
              <Checkbox
                checked={reprintFiltered().length > 0 && reprintFiltered().every((s) => reprintSelected[s.id])}
                onCheckedChange={(v) => toggleReprintSelectAll(!!v)}
                data-testid="staff-reprint-selectall"
              />
              {t('staff.credSelectAll', 'Select All')}
            </label>
            <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="staff-reprint-selected-count">{reprintSelectedIds.length} {t('staff.credSelected', 'selected')}</span>
          </div>

          <div className="flex-1 overflow-y-auto bg-muted/40 rounded-lg p-4">
            {reprintLoading ? (
              <div className="py-8"><LoadingState /></div>
            ) : reprintFiltered().length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                {reprintList.length === 0
                  ? t('staff.reprintEmpty', 'No teachers have generated credentials yet. Use "Generate Credentials" first.')
                  : t('staff.reprintNoMatch', 'No teachers match your search.')}
              </div>
            ) : (
              <div className="flex flex-wrap gap-5 justify-center">
                {reprintFiltered().map((c) => (
                  <div key={c.id} className="relative" data-testid={`staff-reprint-card-${c.id}`}>
                    <label className="absolute -top-2 -left-2 z-10 bg-background border border-border rounded-md p-1.5 shadow-sm cursor-pointer">
                      <Checkbox checked={!!reprintSelected[c.id]} onCheckedChange={() => setReprintSelected((p) => ({ ...p, [c.id]: !p[c.id] }))} data-testid={`staff-reprint-check-${c.id}`} />
                    </label>
                    <div
                      className={reprintSelected[c.id] ? 'ring-2 ring-primary ring-offset-2 ring-offset-background rounded-2xl transition-shadow' : 'opacity-60 transition-opacity'}
                      dangerouslySetInnerHTML={{ __html: cardMarkup(currentOrg, c) }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReprintOpen(false)}>{t('common.close', 'Close')}</Button>
            <Button onClick={printSheetNow} disabled={reprintSelectedIds.length === 0} className="gap-2" data-testid="staff-reprint-print-button">
              <Printer className="w-4 h-4" /> {t('staff.printSelected', 'Print')} ({reprintSelectedIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between border-b border-border py-1.5"><span className="text-muted-foreground">{label}</span><span className="font-medium">{value || '-'}</span></div>
);
